import { tabOptions$, TabOptionsStreamShape } from './tabOptions$';
import { MAIN_PANE_LEFT } from 'streams/views/mainContent/types';
import { ViewIds } from '@ardoq/api-types';
import {
  persistentReducedStream,
  streamReducer,
  dispatchAction,
} from '@ardoq/rxbeach';
import {
  debounceTime,
  distinctUntilChanged,
  scan,
  shareReplay,
  tap,
} from 'rxjs/operators';
import { ActiveTabsAndTabOptions, TabOptions } from './types';
import {
  setActiveTab,
  getNeedsToBeSaved,
  metaInfoListToTabs,
  canDisplayTab,
  sanitizeActiveTabs,
} from './tabUtils';
import { isEqual } from 'lodash';
import { requestSaveViews } from './actions';
import { contextWorkspaceId$ } from 'streams/context/context$';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { isPresentationMode } from 'env';
import { currentUserPermissionContext$ } from '../streams/currentUserPermissions/currentUserPermissionContext$';
import { combineLatest, map, Observable } from 'rxjs';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';
import { requestedTabs$ } from './requestedTabs$';
import type { HasViewpointMode } from '@ardoq/graph';
import { WorkspaceModeViewKey } from 'views/environmentals/viewSettings/types';
import { isWorkspaceModeView } from 'views/environmentals/viewSettings/utils';

/*
  The logic to manage the default views and to select the active view is
  separated in two main use cases:
    * Open the first workspace
    * Open an additional workspace or close an open workspace of a set of
      open workspaces


  Open the first workspace
  ------------------------
  This can be either the very first time to open a workspace or after closing
  all workspaces to open another workspace during a session. There are three
  views to be considered:

    * The last active view during the session
    * The start view
    * The first view of all selected views (of all tabs). This list is the
      pages view plus the default views stored on the active workspace.
      The first one is always the pages view. The order is hardcoded, given by
      the order in metaInfoTabs.

  In this situation the precedence for the first (or main) pane is:
    * Start view (perhaps it’s not in the default views, then it has to be added)
    * Previous active view if that view is in the default views
    * First view of all selected views (pages view)

  Precedence for the second pane:
    * The first tab of the selected views which is not the same view as in the
      main pane.

  Open an additional workspace or close the active workspace of a set of open workspaces
  --------------------------------------------------------------------------------------
  This is for all situations the same: keep all active views (first and optional
  secondary pane) and add them to the default view of the newly active workspace
  (if they are missing).

*/

const DEFAULT_STATE: ActiveTabsAndTabOptions = {
  tabs: [],
  activeTabIdLeft: ViewIds.NONE,
  activeTabIdRight: ViewIds.NONE,
  previousActiveTabIdLeft: ViewIds.NONE,
  metaInfoList: [],
  activeWorkspaceId: '',
  canEditWorkspaceByPermissions: false,
  focusedChrome: focusedChromeOperations.NO_FOCUSED_CHROME,
  needsToBeSaved: false,
};

const toStateWhileInViewpointMode = (
  state: ActiveTabsAndTabOptions,
  {
    activeTabIdLeft: requestedTabIdLeft,
    canEditWorkspaceByPermissions,
    focusedChrome,
  }: TabsSourceDataShape
): ActiveTabsAndTabOptions => ({
  ...state,
  tabs: [],
  needsToBeSaved: false,
  activeTabIdLeft: ViewIds.NONE,
  activeTabIdRight: ViewIds.NONE,
  previousActiveTabIdLeft:
    requestedTabIdLeft ||
    state.activeTabIdLeft ||
    state.previousActiveTabIdLeft,
  canEditWorkspaceByPermissions,
  focusedChrome,
});

const tabOptionsReducer = (
  state: ActiveTabsAndTabOptions,
  tabsSourceData: TabsSourceDataShape
): ActiveTabsAndTabOptions => {
  const {
    activeTabIdLeft: requestedTabIdLeft,
    activeTabIdRight: requestedTabIdRight,
    canEditWorkspaceByPermissions,
    metaInfoList,
    openedWorkspaceStartView,
    viewsAvailableInScope,
    isScenarioMode,
    isViewpointMode,
    focusedChrome,
    isRestrictedToQuickStartViews,
    isHardNaxEnabled,
  } = tabsSourceData;

  if (isViewpointMode) {
    return toStateWhileInViewpointMode(state, tabsSourceData);
  }

  // Apply logic in regards to disabled tabs and restricted views when switching between various modes
  const { activeTabIdLeft, activeTabIdRight } = sanitizeActiveTabs({
    requestedTabIdLeft,
    requestedTabIdRight,
    isScenarioMode,
    focusedChrome,
    state,
    availableViewIds: metaInfoList.map(({ id }) => id),
    isRestrictedToQuickStartViews,
  });

  const hasActiveTabLeft = activeTabIdLeft !== ViewIds.NONE;

  const previousActiveTabIdLeft = !activeTabIdLeft
    ? state.activeTabIdLeft || state.previousActiveTabIdLeft
    : activeTabIdLeft;

  const activeTabIds = [activeTabIdLeft, activeTabIdRight].filter(
    id => id !== ViewIds.NONE
  );

  const tabs = metaInfoListToTabs(
    metaInfoList.filter(metaInfo =>
      canDisplayTab(metaInfo, activeTabIds, viewsAvailableInScope)
    )
  );

  // from here we should trust the tabs to contain all the needed and correct values

  const newState: ActiveTabsAndTabOptions = {
    ...state,
    tabs,
    needsToBeSaved: false,
    activeTabIdLeft,
    activeTabIdRight,
    previousActiveTabIdLeft,
    canEditWorkspaceByPermissions,
    focusedChrome,
  };

  if (tabs.length === 0) {
    return newState;
  }

  const tabIds = new Set(tabs.map(({ id }) => id));

  const firstRandomTabId = tabs[0]?.id;

  // openedWorkspaceStartView is set only when the workspace is opened from the asset browser
  // i.e. if we are not in viewpoint mode or presentation mode
  // if both are falsy, we set what we can and wait for the activeTabIdLeft
  if (!hasActiveTabLeft && !openedWorkspaceStartView) {
    const newActiveTabIdLeft =
      previousActiveTabIdLeft && tabIds.has(previousActiveTabIdLeft)
        ? previousActiveTabIdLeft
        : (firstRandomTabId ?? ViewIds.NONE);

    return {
      ...newState,
      activeTabIdLeft: newActiveTabIdLeft,
    };
  }

  const startView = openedWorkspaceStartView ?? firstRandomTabId;

  // activeTabIdLeft, if set, should override the openedWorkspaceStartView.
  const activeViews = hasActiveTabLeft ? activeTabIds : [startView];

  // if the active tab was not determined by the views awailable in workspace, we add it to the workspace views // Why??
  const needsToBeSaved = getNeedsToBeSaved({
    activeViews,
    viewsAvailableInScope,
    isPresentationMode: isPresentationMode(),
    isHardNaxEnabled,
  });

  return {
    ...newState,
    needsToBeSaved,
    activeTabIdLeft: hasActiveTabLeft ? activeTabIdLeft : startView,
  };
};

const requestSaveWorkspaceIfNeeded = ({
  canEditWorkspaceByPermissions,
  needsToBeSaved,
  activeWorkspaceId: workspaceId,
  tabs,
}: ActiveTabsAndTabOptions) => {
  if (
    canEditWorkspaceByPermissions &&
    needsToBeSaved &&
    workspaceId &&
    tabs.length > 0
  ) {
    const views = tabs.map(({ id }) => id);
    dispatchAction(requestSaveViews({ workspaceId, views }));
  }
};

const canEditWorkspace$: Observable<boolean> = combineLatest([
  currentUserPermissionContext$,
  contextWorkspaceId$,
  activeScenario$,
]).pipe(
  map(([currentUserPermissionContext, { workspaceId }, activeScenarioState]) =>
    workspaceAccessControlInterface.canEditWorkspace(
      currentUserPermissionContext,
      workspaceId,
      activeScenarioState
    )
  ),
  distinctUntilChanged()
);

type TabsSourceDataShape = {
  isScenarioMode: boolean;
} & HasViewpointMode &
  TabOptionsStreamShape &
  Pick<
    ActiveTabsAndTabOptions,
    'activeTabIdLeft' | 'activeTabIdRight' | 'canEditWorkspaceByPermissions'
  >;
const tabsSourceData$: Observable<TabsSourceDataShape> = combineLatest([
  requestedTabs$,
  canEditWorkspace$,
  tabOptions$,
  activeScenario$,
]).pipe(
  map(
    ([
      requestedTabs,
      canEditWorkspaceByPermissions,
      tabOptionsData,
      { isScenarioMode },
    ]) => ({
      ...tabOptionsData,
      activeTabIdLeft: requestedTabs.activeTabIdLeft,
      activeTabIdRight: requestedTabs.activeTabIdRight,
      isViewpointMode: requestedTabs.isViewpointMode,
      canEditWorkspaceByPermissions,
      isScenarioMode,
    })
  ),
  distinctUntilChanged((a, b) => isEqual(a, b)),
  debounceTime(DEFAULT_DEBOUNCE_TIME)
);

const activeTabsAndTabOptions$: Observable<ActiveTabsAndTabOptions> =
  tabsSourceData$.pipe(
    scan(tabOptionsReducer, DEFAULT_STATE),
    distinctUntilChanged<ActiveTabsAndTabOptions>(isEqual),
    tap(requestSaveWorkspaceIfNeeded),
    shareReplay({ bufferSize: 1, refCount: true })
  );

const toWorkspaceModeTabId = (
  viewId: ViewIds
): WorkspaceModeViewKey | ViewIds.NONE =>
  isWorkspaceModeView(viewId) ? viewId : ViewIds.NONE;

const getHandleActiveTabsAndTabOptionsReducer =
  (activeTabField: 'activeTabIdLeft' | 'activeTabIdRight') =>
  (
    state: TabOptions,
    {
      tabs,
      [activeTabField]: activeTabId,
      canEditWorkspaceByPermissions,
      focusedChrome,
    }: ActiveTabsAndTabOptions
  ): TabOptions => ({
    ...state,
    canEditWorkspace: canEditWorkspaceByPermissions,
    focusedChrome,
    tabs: setActiveTab(tabs, activeTabId),
    activeTabId: toWorkspaceModeTabId(activeTabId),
  });

const DEFAULT_TAB_OPTIONS: TabOptions = {
  tabs: [],
  activeTabId: ViewIds.NONE,
  canEditWorkspace: false,
  focusedChrome: focusedChromeOperations.NO_FOCUSED_CHROME,
};

const handleActiveTabsAndTabOptionsLeft =
  getHandleActiveTabsAndTabOptionsReducer('activeTabIdLeft');
const handleActiveTabsAndTabOptionsRight =
  getHandleActiveTabsAndTabOptionsReducer('activeTabIdRight');

export const leftTabOptions$ = persistentReducedStream<TabOptions>(
  'leftTabOptions$',
  DEFAULT_TAB_OPTIONS,
  [streamReducer(activeTabsAndTabOptions$, handleActiveTabsAndTabOptionsLeft)]
);

export const rightTabOptions$ = persistentReducedStream<TabOptions>(
  'rightTabOptions$',
  DEFAULT_TAB_OPTIONS,
  [streamReducer(activeTabsAndTabOptions$, handleActiveTabsAndTabOptionsRight)]
);

export const getTabOptionsStreamWithLocation = (location: string) =>
  location === MAIN_PANE_LEFT ? leftTabOptions$ : rightTabOptions$;
